import { useIntl } from "react-intl";

import {
  VerticalRectangleContainer,
  VerticalRectangleImage,
  VerticalRectangleMask,
} from "./styles";

import Labels from "../Labels";
import Logo from "../Logo";

import {
  getColor,
  pickVerticalRectangleDefaultPositions,
  pickVerticalRectangleDefaultSizes,
  style,
} from "../shapes-utils";

const ratio = 9 / 16;

export function VerticalRectangle01({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 1,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 1,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={1}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2070_3908)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1080 0H0v719.267c125.85-46.402 264.824-67.342 409.228-56.604C685.045 683.173 924.18 815.049 1080 1007.53z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).fillOpacity}
            fillRule="evenodd"
            d="M156.877 2500.09c579.866 117.96 1138.553-221.68 1247.853-758.62 109.3-536.93-272.17-1067.836-852.032-1185.802C-27.168 437.702-585.851 777.344-695.154 1314.28c-109.303 536.94 272.164 1067.84 852.031 1185.81m25.789-121.48c501.283 101.98 985.204-196.31 1080.864-666.24 95.67-469.93-233.15-933.559-734.437-1035.538C27.81 574.852-456.111 873.137-551.774 1343.07c-95.663 469.93 233.157 933.56 734.44 1035.54"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            stroke={getColor(variantColor).secondary}
            strokeWidth="29.1"
            d="M1246.29 1632.77c-59.45 467.15-511.737 795.99-1011.932 732.33s-855.696-495.3-796.245-962.44c59.451-467.15 511.736-795.988 1011.93-732.331s855.697 495.301 796.247 962.441Z"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle01"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle02({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 2,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 2,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={2}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2070_3622)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M1080 0H0v1920h353V740h727z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M491.653-86.999C491.653-4.289 424.218 63 341.327 63S191-4.287 191-86.999 258.436-237 341.327-237s150.326 67.293 150.326 150.001"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M1027.16 544.687a3.449 3.449 0 0 1-5.89 2.438 3.45 3.45 0 0 1-.75-3.759 3.455 3.455 0 0 1 5.63-1.118 3.48 3.48 0 0 1 1.01 2.439m-26.96 0a3.453 3.453 0 0 1-3.454 3.449 3.449 3.449 0 1 1 0-6.898 3.453 3.453 0 0 1 3.454 3.449m-26.967 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.262-25.077a3.449 3.449 0 0 1-5.89 2.438 3.45 3.45 0 0 1-.75-3.759 3.455 3.455 0 0 1 3.19-2.128 3.454 3.454 0 0 1 3.45 3.449m-26.96 0a3.453 3.453 0 0 1-3.454 3.449 3.449 3.449 0 1 1 0-6.898 3.453 3.453 0 0 1 3.454 3.449m-26.967 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.262-25.081c0 1.395-.84 2.653-2.13 3.187a3.45 3.45 0 0 1-4.51-4.506 3.44 3.44 0 0 1 3.19-2.129 3.46 3.46 0 0 1 3.45 3.444zm-26.96 0a3.455 3.455 0 0 1-5.892 2.44 3.448 3.448 0 0 1 2.438-5.888 3.46 3.46 0 0 1 3.454 3.444zm-26.967 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004zm80.262-25.081c0 1.395-.84 2.653-2.13 3.187a3.45 3.45 0 0 1-4.51-4.506 3.44 3.44 0 0 1 3.19-2.129 3.46 3.46 0 0 1 3.45 3.444zm-26.96 0a3.455 3.455 0 0 1-5.892 2.44 3.448 3.448 0 0 1 2.438-5.888 3.46 3.46 0 0 1 3.454 3.444zm-26.967 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M1023.66 302.258H856V295h167.66z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.04"
            fillRule="evenodd"
            d="m-171.999 1576.1 423.22 442.75v48.77l-423.22-442.75zm0 114.62v48.82l360.128 376.74h46.653zm-.001 163.47v-48.82l297.186 310.91H78.534zm.001 65.86v48.79l140.939 147.44h46.637zm0 114.69v48.75l31.345 32.79h46.607zm423.22-130.56-423.22-442.75v48.77l423.22 442.75zm-423.22-557.42 423.22 442.75v48.79l-423.22-442.75zm423.22 328.08-423.22-442.75v48.79l423.22 442.77zm-423.22-557.4 423.22 442.75V1609l-423.22-442.77zm423.22 328.1-423.22-442.75v48.77l423.22 442.77zm-423.22-557.424 423.22 442.774v48.77l-423.22-442.819zm423.22 328.104L-101.671 847h-46.607l399.499 417.99zM7.939 847l243.282 254.55v48.79L-38.714 847zM251.22 986.879 117.518 847H70.896l180.325 188.67zM227.128 847l24.093 25.253V921l-70.716-74z"
            clipRule="evenodd"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle02"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle03({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 3,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 3,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={3}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2070_3686)">
          <path
            fill={getColor(variantColor).primary}
            style={style}
            fillRule="evenodd"
            d="M1080 0H.001v1920h255v-726.15L548.652 667H1080z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="m132.21 81.709-171 117"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M-111.4 1147.19 255.35 926.1z"
          ></path>
          <path
            style={style}
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="M-111.4 1147.19 255.35 926.1"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M132 837.5 106 855v-35zM677.655 87.359 638 113.718V61zM240.646 60.949 200.99 87.308V34.59zM897.738 304.42 861 328.84V280z"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle03"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle04({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 4,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 4,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={4}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2137_3305)">
          <path
            fill={getColor(variantColor).primary}
            style={style}
            d="M347.426 0H0v1920h1080v-828.91c-270.672 30.23-722.888-328.704-808.707-672.967C228.924 248.161 262.531 109.167 347.426 0"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M1353.3-205.642c-145.8-90.094-340.3-105.437-547.834-53.684-364.481 90.892-623.034 312.96-532.173 677.449 33.547 134.573 123.08 271.387 235.871 386.322-168.592-130.474-315.632-316.016-360.868-497.482-90.861-364.49 167.691-586.558 532.173-677.449 274.667-68.495 526.511-19.462 672.831 164.844"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M247.837 1089.57a5.16 5.16 0 0 0-5.164 5.16 5.17 5.17 0 0 0 5.164 5.17 5.17 5.17 0 0 0 5.163-5.17c0-2.85-2.311-5.16-5.163-5.16m-37.935 0a5.16 5.16 0 0 0-5.164 5.16 5.17 5.17 0 0 0 5.164 5.17 5.17 5.17 0 0 0 5.163-5.17c0-2.85-2.311-5.16-5.163-5.16m-37.934 0a5.16 5.16 0 0 0-5.164 5.16 5.17 5.17 0 0 0 5.164 5.17 5.17 5.17 0 0 0 5.163-5.17c0-2.85-2.312-5.16-5.163-5.16m-37.935 0a5.16 5.16 0 0 0-5.164 5.16 5.17 5.17 0 0 0 5.164 5.17 5.17 5.17 0 0 0 5.164-5.17c0-2.85-2.312-5.16-5.164-5.16m-37.935 0a5.16 5.16 0 0 0-5.163 5.16 5.17 5.17 0 0 0 5.163 5.17 5.17 5.17 0 0 0 5.164-5.17c0-2.85-2.312-5.16-5.164-5.16m-37.934 0a5.16 5.16 0 0 0-5.164 5.16 5.17 5.17 0 0 0 5.163 5.17 5.17 5.17 0 0 0 5.164-5.17c0-2.85-2.311-5.16-5.163-5.16m189.673-30.39a5.16 5.16 0 0 0-5.164 5.16c0 2.85 2.312 5.16 5.164 5.16a5.161 5.161 0 1 0 0-10.32m-37.935 0a5.16 5.16 0 0 0-5.164 5.16c0 2.85 2.312 5.16 5.164 5.16a5.161 5.161 0 1 0 0-10.32m-37.934 0a5.16 5.16 0 0 0-5.164 5.16c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16c0-2.85-2.312-5.16-5.163-5.16m-37.935 0a5.16 5.16 0 0 0-5.164 5.16c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.164-5.16c0-2.85-2.312-5.16-5.164-5.16m-37.935 0a5.16 5.16 0 0 0-5.163 5.16c0 2.85 2.311 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16c0-2.85-2.312-5.16-5.164-5.16m-37.934 0a5.16 5.16 0 0 0-5.164 5.16c0 2.85 2.312 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16c0-2.85-2.311-5.16-5.163-5.16m189.673-30.4a5.17 5.17 0 0 0-5.164 5.17c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.17 5.17 0 0 0-5.163-5.17m-37.935 0a5.17 5.17 0 0 0-5.164 5.17c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.17 5.17 0 0 0-5.163-5.17m-37.934 0a5.17 5.17 0 0 0-5.164 5.17c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.17 5.17 0 0 0-5.163-5.17m-37.935 0a5.17 5.17 0 0 0-5.164 5.17c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.164-5.16 5.17 5.17 0 0 0-5.164-5.17m-37.935 0a5.17 5.17 0 0 0-5.163 5.17c0 2.85 2.311 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16 5.17 5.17 0 0 0-5.164-5.17m-37.934 0a5.17 5.17 0 0 0-5.164 5.17c0 2.85 2.312 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16 5.17 5.17 0 0 0-5.163-5.17m189.673-30.388a5.16 5.16 0 0 0-5.164 5.168c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.16 5.16 0 0 0-5.163-5.168m-37.935 0a5.16 5.16 0 0 0-5.164 5.168c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.16 5.16 0 0 0-5.163-5.168m-37.934 0a5.16 5.16 0 0 0-5.164 5.168c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.163-5.16 5.16 5.16 0 0 0-5.163-5.168m-37.935 0a5.16 5.16 0 0 0-5.164 5.168c0 2.85 2.312 5.16 5.164 5.16a5.16 5.16 0 0 0 5.164-5.16 5.16 5.16 0 0 0-5.164-5.168m-37.935 0a5.16 5.16 0 0 0-5.163 5.168c0 2.85 2.311 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16 5.16 5.16 0 0 0-5.164-5.168m-37.934 0A5.16 5.16 0 0 0 53 1003.56c0 2.85 2.312 5.16 5.163 5.16a5.16 5.16 0 0 0 5.164-5.16 5.16 5.16 0 0 0-5.163-5.168m189.673-20.064a5.164 5.164 0 1 0-.002-10.328 5.164 5.164 0 0 0 .002 10.328M209.902 968a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.934 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 0 10.327 5.164 5.164 0 0 0 0-10.327m-32.771 5.163a5.164 5.164 0 1 1-10.329.001 5.164 5.164 0 0 1 10.329-.001M-79 594c-39.7 0-72 32.3-72 72s32.3 72 72 72 72-32.3 72-72-32.3-72-72-72m0 204c-72.784 0-132-59.216-132-132s59.216-132 132-132S53 593.216 53 666-6.216 798-79 798"
            clipRule="evenodd"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle04"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle05({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 5,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 5,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={5}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2137_3448)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1920h1080v-177.35c-172.769 75.83-356.568 54.98-511.356-100.1-224.295-224.73-236.704-715.614-82.685-889.152C586.835 639.738 848.454 625.27 1080 691.953z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M427.215 852.998c-83.351 68.004-140.614 145.244-152.111 221.432-36.719 243.34 257.922 672.89 587.232 743.27 329.314 70.38 549.954-145.18 619.604-474.64 1.28-6.06 2.51-12.11 3.69-18.14-37.45 90.17-101.43 178.59-183.19 260.5-224.29 224.72-509.502 281.85-733.796 57.13-190.052-190.42-227.976-571.93-141.429-789.552M857.839 653.638c195.021 11.521 411.431 84.66 535.171 208.639 48.07 48.159 81.68 98.567 102.6 150.263-31.2-181.038-138.87-319.805-350.91-365.121-87.35-18.668-186.956-14.51-286.861 6.219"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M578.94 1651.68c.518.5 1.035.99 1.553 1.49-40.864 18.84-86.666 29.92-132.409 33.52-53.025 4.18-106.05-1.67-151.345-17.12-45.287-15.45-82.92-40.53-105.035-74.88-43.761-67.96-91.377-87.24-127.938-89.65-18.31-1.2-33.898 1.82-44.908 5.14-5.504 1.66-9.86 3.4-12.836 4.72-1.487.66-2.629 1.21-3.396 1.6q-.576.285-.865.45c-.096.05-.168.09-.215.11l-.052.03-.012.01H1.48l-.491-.87c-.491-.87-.489-.87-.489-.87l.005-.01H.52l.062-.04c.053-.03.131-.07.234-.13.206-.11.51-.27.907-.47.795-.4 1.968-.97 3.488-1.64 3.041-1.35 7.475-3.11 13.068-4.8 11.184-3.38 27.016-6.45 45.618-5.22 37.26 2.45 85.422 22.13 129.487 90.56 21.798 33.85 58.988 58.71 104 74.06 45.004 15.36 97.752 21.19 150.541 17.03 45.314-3.57 90.598-14.49 131.014-33.02"
            opacity="0.23"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            fillRule="evenodd"
            d="M303.656 1810.6c4.202 4.2 9.791 6.52 15.734 6.52 5.944 0 11.532-2.32 15.734-6.52l3.437-3.44a18.16 18.16 0 0 1 12.907-5.34c4.875 0 9.459 1.9 12.906 5.34l1.533 1.54c4.203 4.2 9.791 6.51 15.734 6.51 5.944 0 11.532-2.31 15.735-6.51l1.222-1.23c7.117-7.11 18.696-7.11 25.813 0 8.676 8.68 22.792 8.68 31.469 0l1.139-1.14c7.119-7.11 18.698-7.11 25.813 0 .78.79 2.048.79 2.828 0a1.99 1.99 0 0 0 0-2.82c-8.676-8.68-22.793-8.68-31.469 0l-1.139 1.14c-7.118 7.11-18.697 7.11-25.813 0-8.676-8.68-22.794-8.68-31.469 0l-1.222 1.22c-3.448 3.45-8.031 5.34-12.907 5.34s-9.459-1.89-12.906-5.34l-1.533-1.53c-4.203-4.21-9.791-6.52-15.734-6.52-5.944 0-11.532 2.31-15.735 6.52l-3.437 3.43a18.13 18.13 0 0 1-12.906 5.35c-4.875 0-9.458-1.9-12.906-5.35l-2.429-2.43c-8.676-8.67-22.793-8.67-31.469 0a2.001 2.001 0 0 0 2.828 2.83c7.118-7.11 18.697-7.11 25.812 0zm0 48c4.202 4.2 9.791 6.52 15.734 6.52 5.944 0 11.532-2.32 15.734-6.52l3.437-3.44a18.16 18.16 0 0 1 12.907-5.34c4.875 0 9.459 1.9 12.906 5.34l1.533 1.54c4.203 4.2 9.791 6.51 15.734 6.51 5.944 0 11.532-2.31 15.735-6.51l1.222-1.23c7.117-7.11 18.696-7.11 25.813 0 8.676 8.68 22.792 8.68 31.469 0l1.139-1.14c7.119-7.11 18.698-7.11 25.813 0 .78.79 2.048.79 2.828 0a1.99 1.99 0 0 0 0-2.82c-8.676-8.68-22.793-8.68-31.469 0l-1.139 1.14c-7.118 7.11-18.697 7.11-25.813 0-8.676-8.68-22.794-8.68-31.469 0l-1.222 1.22c-3.448 3.45-8.031 5.34-12.907 5.34s-9.459-1.89-12.906-5.34l-1.533-1.53c-4.203-4.21-9.791-6.52-15.734-6.52-5.944 0-11.532 2.31-15.735 6.52l-3.437 3.43a18.13 18.13 0 0 1-12.906 5.35c-4.875 0-9.458-1.9-12.906-5.35l-2.429-2.43c-8.676-8.67-22.793-8.67-31.469 0a2.001 2.001 0 0 0 2.828 2.83c7.118-7.11 18.697-7.11 25.812 0z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M1008.84 226.568a5.17 5.17 0 0 0-5.17 5.164 5.17 5.17 0 0 0 5.17 5.163c2.85 0 5.16-2.311 5.16-5.163a5.16 5.16 0 0 0-5.16-5.164m0-38a5.17 5.17 0 0 0-5.17 5.164 5.17 5.17 0 0 0 5.17 5.163c2.85 0 5.16-2.311 5.16-5.163a5.16 5.16 0 0 0-5.16-5.164m-37.938 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m37.938-30.392a5.17 5.17 0 0 0-5.17 5.163 5.17 5.17 0 0 0 5.17 5.164c2.85 0 5.16-2.311 5.16-5.164a5.16 5.16 0 0 0-5.16-5.163m-37.938 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.934 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m75.872-30.391a5.17 5.17 0 0 0-5.17 5.163 5.17 5.17 0 0 0 5.17 5.163c2.85 0 5.16-2.312 5.16-5.163a5.16 5.16 0 0 0-5.16-5.163m-37.938 0a5.163 5.163 0 1 0-.002 10.326 5.163 5.163 0 0 0 .002-10.326m-37.934 0a5.163 5.163 0 1 0-.002 10.326 5.163 5.163 0 0 0 .002-10.326m-37.935 0a5.163 5.163 0 1 0 0 10.326 5.164 5.164 0 1 0 0-10.326m113.807-30.393a5.17 5.17 0 0 0-5.17 5.164 5.17 5.17 0 0 0 5.17 5.163c2.85 0 5.16-2.311 5.16-5.163a5.16 5.16 0 0 0-5.16-5.164m-37.938 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.934 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 .002 10.328 5.164 5.164 0 0 0-.002-10.328m151.742-20.064c2.85 0 5.16-2.312 5.16-5.165a5.16 5.16 0 0 0-5.16-5.163 5.17 5.17 0 0 0-5.17 5.163 5.17 5.17 0 0 0 5.17 5.165M970.902 67a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.934 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 0 10.328 5.164 5.164 0 0 0 0-10.328m-37.935 0a5.164 5.164 0 1 0 .001 10.327A5.164 5.164 0 0 0 857.098 67m-32.771 5.163a5.164 5.164 0 1 1-10.328 0 5.164 5.164 0 0 1 10.328 0M0 1008c-39.7 0-72 32.3-72 72s32.3 72 72 72 72-32.3 72-72-32.3-72-72-72m0 204c-72.784 0-132-59.22-132-132S-72.784 948 0 948s132 59.22 132 132-59.216 132-132 132M189.296 727l-14.792 14.792-14.8-14.792-5.704 5.704 14.8 14.792-14.8 14.8 5.704 5.704 14.8-14.8 14.792 14.8 5.704-5.704-14.796-14.8L195 732.704z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="M684.422 152S791.43 286.28 702.96 470.09s-423.578 193.447-511.404 57.059C103.73 390.762 0 449.229 0 449.229"
            opacity="0.23"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle05"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle06({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 6,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 6,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={6}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2070_3756)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M667.213 579.894c41.578-15.81 90.837-28.722 149.89-40.806C1030.42 495.435 1073.77 220.215 1080 52.035v258.341C1040.44 420.161 968.762 527.932 839.16 544.3c-73.846 9.326-127.993 20.487-171.947 35.594"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M442.356 859.884c27.073-83.267 54.158-140.59 89.818-183.811 58.432-70.823 141.772-104.935 307.49-125.865 72.189-9.118 132.898-46.501 180.446-111.113 23.1-31.391 43.12-69.215 59.89-113.034V1920H0v-514.19c28.79-10.16 59.226-17.93 89.947-25.77 128.062-32.68 261.095-66.63 300.718-280.25 17.919-96.6 33.815-175.26 51.691-239.906"
          ></path>
          <ellipse
            cx="1080.5"
            cy="716.002"
            stroke={getColor(variantColor).secondary}
            strokeWidth="50"
            rx="134.5"
            ry="127"
          ></ellipse>
          <circle
            cx="660"
            cy="1227"
            r="16"
            fill={getColor(variantColor).secondary}
          ></circle>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M760.808 747.216a16 16 0 1 1 18.061 13.63c-8.751 1.223-16.837-4.879-18.061-13.63"
            opacity="0.502"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M261.155 1769.22a16 16 0 1 1 18.061 13.63c-8.751 1.22-16.838-4.88-18.061-13.63"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.1"
            fillRule="evenodd"
            d="M813.103 1005.39c6.916 6.92 16.116 10.72 25.897 10.72 9.784 0 18.981-3.8 25.897-10.72l5.657-5.66c5.676-5.673 13.221-8.799 21.245-8.799 8.023 0 15.568 3.126 21.242 8.799l2.523 2.52c6.918 6.92 16.115 10.73 25.897 10.73 9.784 0 18.981-3.81 25.899-10.73l2.011-2.01c11.714-11.71 30.769-11.712 42.489 0 14.28 14.28 37.51 14.28 51.79 0l1.88-1.876c11.72-11.711 30.77-11.711 42.48 0a3.295 3.295 0 0 0 4.66 0 3.3 3.3 0 0 0 0-4.655c-14.28-14.28-37.52-14.278-51.8 0l-1.87 1.877c-11.72 11.714-30.78 11.714-42.49 0-14.28-14.28-37.515-14.28-51.793 0l-2.012 2.011c-5.675 5.673-13.218 8.803-21.244 8.803-8.024 0-15.569-3.13-21.242-8.803l-2.523-2.521c-6.918-6.918-16.116-10.729-25.897-10.729-9.784 0-18.981 3.811-25.899 10.727l-5.657 5.656c-5.674 5.68-13.219 8.8-21.243 8.8s-15.567-3.12-21.242-8.8l-3.998-3.997c-14.28-14.28-37.516-14.282-51.796 0a3.295 3.295 0 0 0 0 4.657 3.3 3.3 0 0 0 4.655 0c11.716-11.715 30.774-11.713 42.485 0zm0 79c6.916 6.92 16.116 10.73 25.897 10.73 9.784 0 18.981-3.81 25.897-10.73l5.657-5.65c5.676-5.68 13.221-8.8 21.245-8.8 8.023 0 15.568 3.12 21.242 8.8l2.523 2.52c6.918 6.92 16.115 10.72 25.897 10.72 9.784 0 18.981-3.8 25.899-10.72l2.011-2.01c11.714-11.72 30.769-11.72 42.489 0 14.28 14.28 37.51 14.28 51.79 0l1.88-1.88c11.72-11.71 30.77-11.71 42.48 0a3.29 3.29 0 0 0 4.66 0 3.31 3.31 0 0 0 0-4.66c-14.28-14.28-37.52-14.27-51.8 0l-1.87 1.88c-11.72 11.72-30.78 11.71-42.49 0-14.28-14.28-37.515-14.28-51.793 0l-2.012 2.01c-5.675 5.68-13.218 8.8-21.244 8.8-8.024 0-15.569-3.12-21.242-8.8l-2.523-2.52c-6.918-6.92-16.116-10.73-25.897-10.73-9.784 0-18.981 3.81-25.899 10.73l-5.657 5.66c-5.674 5.67-13.219 8.79-21.243 8.79s-15.567-3.12-21.242-8.79l-3.998-4c-14.28-14.28-37.516-14.28-51.796 0a3.286 3.286 0 0 0 0 4.65 3.283 3.283 0 0 0 4.655 0c11.716-11.71 30.774-11.71 42.485 0z"
            clipRule="evenodd"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle06"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle07({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 7,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 7,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={7}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2137_3638)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1920h736.454a308.7 308.7 0 0 1-71.49-53.37l-468.596-468.59a308.63 308.63 0 0 1-90.595-218.43c0-81.95 32.593-160.53 90.593-218.428l679.401-679.401A308.63 308.63 0 0 1 1080 191.518zM1080 1886.49a308.4 308.4 0 0 1-49.68 33.51H1080z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M943.53 110.47 684.47 369.531a233.26 233.26 0 0 0-68.317 173.544l259.723-259.723a308.6 308.6 0 0 1 218.424-90.591 308.6 308.6 0 0 1 218.42 90.59l228.78 228.78a232 232 0 0 0 2.59-34.655 233.24 233.24 0 0 0-68.47-165.088L1273.71 110.47A233.28 233.28 0 0 0 1108.62 42a233.26 233.26 0 0 0-165.09 68.47"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).quaternary}
            fillOpacity="0.1"
            d="M1129.82-605.631a207.3 207.3 0 0 1 283.44 75.95l.01.002 194 336.018v.002a207.33 207.33 0 0 1 20.8 157.473 207.34 207.34 0 0 1-96.75 125.973v.001l-158.51 91.513 3.66 3.661 157.34-90.842a212.3 212.3 0 0 0 99.09-129.012 212.28 212.28 0 0 0-21.31-161.271L1417.6-532.177a212.33 212.33 0 0 0-129.02-99.088 212.28 212.28 0 0 0-161.26 21.305l-.01.002-13.77 13.723-491.216 283.606-.002.001a207.31 207.31 0 0 0-76.615 282.296l-3.667 3.655 193.998 336.019a213 213 0 0 0 4.181 6.946l3.629-3.628a210 210 0 0 1-3.478-5.814L546.369-29.179l-.001-.002q-.332-.575-.661-1.151l567.833-565.903 16.28-9.394z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).quaternary}
            fillOpacity="0.1"
            d="M1238.32-681.131a207.3 207.3 0 0 1 283.44 75.95l.01.002 194 336.018v.002a207.33 207.33 0 0 1 20.8 157.473 207.34 207.34 0 0 1-96.75 125.973v.002L1481.31 105.8l3.66 3.661 157.34-90.842a212.3 212.3 0 0 0 99.09-129.012 212.28 212.28 0 0 0-21.31-161.271L1526.1-607.677a212.33 212.33 0 0 0-129.02-99.088 212.28 212.28 0 0 0-161.26 21.305l-.01.002-13.77 13.723-491.216 283.606-.002.001a207.31 207.31 0 0 0-76.615 282.296l-3.667 3.655 193.998 336.019a213 213 0 0 0 4.181 6.946l3.629-3.628a210 210 0 0 1-3.478-5.814L654.869-104.679l-.001-.002-.661-1.151 567.833-565.903 16.28-9.394z"
          ></path>
          <path
            stroke={getColor(variantColor).curvedLine}
            style={style}
            opacity={getColor(variantColor).curvedLineOpacity}
            strokeWidth="150"
            d="m-369.794 835.562-293-507.491a209.81 209.81 0 0 1 76.865-286.865l336.018-194A209.805 209.805 0 0 1 36.954-75.929l293 507.491a209.81 209.81 0 0 1-76.865 286.865l-336.018 194a209.81 209.81 0 0 1-286.865-76.865Z"
            clipRule="evenodd"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle07"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle08({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 8,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 8,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={8}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2137_3672)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1080 0H0v1920h388.245C271.395 1761.96 205 1580.74 205 1388.1c0-447.485 358.272-833.321 875-1010.278z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).fillOpacity}
            d="M1427 1935h783.66c82.67-160.38 129.34-342.33 129.34-535.18C2340 753.746 1816.17 230 1170 230 523.827 230 0 753.746 0 1399.82c0 192.85 46.674 374.8 129.343 535.18H399.54C275.683 1773.47 205 1586.87 205 1388.1 205 846.033 730.727 394.432 1427 295z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M285.155 864.216a15.999 15.999 0 0 1 25.484-14.987 16 16 0 0 1-7.423 28.617c-8.751 1.223-16.838-4.879-18.061-13.63"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M0 806c-39.7 0-72 32.3-72 72s32.3 72 72 72 72-32.3 72-72-32.3-72-72-72m0 204c-72.784 0-132-59.216-132-132S-72.784 746 0 746s132 59.216 132 132-59.216 132-132 132"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M771.139 224.083a5.89 5.89 0 0 1 5.917-5.916 5.89 5.89 0 0 1 5.916 5.916 5.89 5.89 0 0 1-5.916 5.917 5.89 5.89 0 0 1-5.917-5.917M934.833 230a5.893 5.893 0 0 0 5.917-5.917 5.89 5.89 0 0 0-5.917-5.916 5.89 5.89 0 0 0-5.916 5.916 5.89 5.89 0 0 0 5.916 5.917m-236.666-11.833a5.89 5.89 0 0 0-5.917 5.916 5.893 5.893 0 0 0 5.917 5.917 5.89 5.89 0 0 0 5.916-5.917 5.89 5.89 0 0 0-5.916-5.916M855.944 230a5.893 5.893 0 0 0 5.917-5.917 5.89 5.89 0 0 0-5.917-5.916 5.89 5.89 0 0 0-5.916 5.916 5.89 5.89 0 0 0 5.916 5.917m78.889-59.167a5.89 5.89 0 0 0 5.917-5.916 5.893 5.893 0 0 0-5.917-5.917 5.89 5.89 0 0 0-5.916 5.917 5.89 5.89 0 0 0 5.916 5.916m-78.889 0a5.89 5.89 0 0 0 5.917-5.916 5.893 5.893 0 0 0-5.917-5.917 5.89 5.89 0 0 0-5.916 5.917 5.89 5.89 0 0 0 5.916 5.916m-78.888 0a5.89 5.89 0 0 0 5.916-5.916 5.89 5.89 0 0 0-5.916-5.917 5.89 5.89 0 0 0-5.917 5.917 5.89 5.89 0 0 0 5.917 5.916m-78.889 0a5.89 5.89 0 0 0 5.916-5.916 5.89 5.89 0 0 0-5.916-5.917 5.893 5.893 0 0 0-5.917 5.917 5.89 5.89 0 0 0 5.917 5.916"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle08"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle09({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 9,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 9,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={9}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <path
          style={style}
          fill={getColor(variantColor).secondary}
          fillRule="evenodd"
          d="M654.48 1920 267.427 960 654.48 0H0v1920z"
          clipRule="evenodd"
        ></path>
        <path
          style={style}
          fill={getColor(variantColor).primary}
          fillRule="evenodd"
          d="M504.437 1567 805 960.501 504.437 354H0v1213z"
          clipRule="evenodd"
        ></path>
        <path
          style={style}
          fill={getColor(variantColor).secondary}
          fillOpacity="0.2"
          d="M342.655 242.359 303 268.718V216zM351.738 820.42 315 844.84V796z"
        ></path>
        <path
          style={style}
          fill={getColor(variantColor).secondary}
          fillOpacity="0.1"
          d="M412 567.5 333 620V515zM294 1367.5l79 52.5v-105z"
        ></path>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle09"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}

export function VerticalRectangle10({
  size = 1080,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const verticalRectangleSizes = pickVerticalRectangleDefaultSizes({
    shape: 10,
    size,
  });
  const verticalRectanglePositions =
    positions ??
    pickVerticalRectangleDefaultPositions({
      shape: 10,
      size,
    });

  return (
    <VerticalRectangleContainer size={size} label={label} shape={10}>
      <VerticalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1080 1920"
      >
        <g clipPath="url(#clip0_2033_5909)">
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            d="M135.655 212.359 96 238.718V186zM351.738 820.42 315 844.84V796z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M952.5 1920H0V0H452.205L952.5 1920Z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).triangleOpacity}
            d="M140 1920H-585V412H-99.2978L140 1920Z"
          ></path>
        </g>
      </VerticalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={verticalRectangleSizes.logoSize}
        top={verticalRectanglePositions.logoPosition.y}
        left={verticalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <VerticalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="verticalRectangle10"
          top={verticalRectanglePositions.imagePosition.y}
          left={verticalRectanglePositions.imagePosition.x}
          size={verticalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={verticalRectanglePositions.labelsPosition.y}
        left={verticalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={verticalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </VerticalRectangleContainer>
  );
}
